import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState, type MouseEvent, type FocusEvent } from "react";

export function isClippedTableText(element: HTMLElement): boolean {
  const style = getComputedStyle(element);
  if (style.textOverflow !== "ellipsis" && !(Number.parseInt(style.webkitLineClamp, 10) > 0)) return false;
  return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
}

/** Find the clipped text under the pointer, without reading record values or hidden fields. */
function clippedCellText(target: EventTarget): { element: HTMLElement; text: string } | undefined {
  if (!(target instanceof Element)) return;
  const cell = target.closest("td, [role='cell']");
  if (!cell || target.closest("[data-mui-internal-clone-element], [data-table-cell-tooltip]")) return;

  for (let element: Element | null = target; element && cell.contains(element); element = element.parentElement) {
    if (!(element instanceof HTMLElement) || element.matches(".MuiIcon-root, .MuiSvgIcon-root, input, textarea")) continue;
    if (element.title.trim()) return;
    if (!isClippedTableText(element)) continue;
    const text = (element.innerText ?? element.textContent ?? "").trim();
    if (text) return { element, text };
  }
}

/** One fallback per table for legacy cells; cells with their own tooltip keep owning it. */
export function useTableCellTooltip() {
  const [revealed, setRevealed] = useState<{ element: HTMLElement; text: string } | undefined>();
  const anchor = revealed?.element;
  useEffect(() => {
    if (!anchor) return;
    const observer = new MutationObserver(() => {
      if (!anchor.isConnected) setRevealed(undefined);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [anchor]);

  function onMouseOver(event: MouseEvent<HTMLElement>) {
    const next = clippedCellText(event.target);
    setRevealed(current => {
      if (!current || !next) return next;
      if (current.element === next.element && current.text === next.text) return current;
      return next;
    });
  }

  function clear() {
    setRevealed(undefined);
  }

  function onFocus(event: FocusEvent<HTMLElement>) {
    setRevealed(clippedCellText(event.target));
  }

  function onMouseOut(event: MouseEvent<HTMLElement>) {
    if (event.relatedTarget instanceof Node && revealed?.element.contains(event.relatedTarget)) return;
    clear();
  }

  return {
    clear,
    onFocus,
    onMouseOver,
    onMouseOut,
    tooltip: (
      <Tooltip open={Boolean(revealed)} title={revealed?.text ?? ""} disableHoverListener
        disableFocusListener disableTouchListener disableInteractive
        slotProps={{ popper: { anchorEl: revealed?.element }, tooltip: { sx: { whiteSpace: "pre-line" } } }}>
        <span aria-hidden="true" />
      </Tooltip>
    ),
  };
}
